import { Sparkles } from "lucide-react";
import type { FC } from "react";

import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";

import { useCaseIde, useOnInvalidate } from "../context/CaseIdeContext";
import { useCaseData } from "../data/CaseDataContext";
import { FileCardClassification } from "../files/FileCardClassification";
import { FileCardSummaryView } from "../files/FileCardSummaryView";
import {
  isCardBuilding,
  unavailableMessage,
  useFileCard
} from "../files/fileCard";

type Props = {
  nodeId: string;
  title: string;
  exhibitLabel?: string;
};

/** What Ask Casey to Edit seeds the composer with, before the file's mention. */
export const EDIT_CARD_PROMPT = "Edit the card for ";

/** A file's card, open as a workspace tab, in language an attorney can review. */
export const FileCardPane: FC<Props> = ({ nodeId, title, exhibitLabel }) => {
  const { individualId, requestPrompt, requestMention } = useCaseIde();
  const { fs } = useCaseData();
  const node = fs.nodes.find((n) => n.id === nodeId);
  const {
    data: card,
    isError,
    refetch
  } = useFileCard(individualId, nodeId, true);
  // Casey's rebuild invalidates the tree; the card's status follows, and
  // the poll takes over while it is rebuilt.
  useOnInvalidate(["fs"], () => void refetch());

  const fileTitle = card?.title || title;
  const askCasey = () => {
    requestPrompt(EDIT_CARD_PROMPT);
    requestMention({ kind: "file", id: nodeId, title: fileTitle });
  };

  let body = (
    <p className="flex items-center gap-2 text-base text-fg-3">
      <Spinner className="size-3.5" />
      Loading…
    </p>
  );
  if (
    card?.markdown &&
    (card.status === "ready" || isCardBuilding(card.status))
  ) {
    body = (
      <div className="flex flex-col gap-3">
        {isCardBuilding(card.status) ? (
          <p
            className="flex items-center gap-2 text-base text-fg-3"
            data-testid="file-card-rebuilding"
          >
            <Spinner className="size-3.5" />
            Rebuilding the card…
          </p>
        ) : null}
        <FileCardSummaryView
          markdown={card.markdown}
          exhibitLabel={exhibitLabel}
          fallbackTitle={fileTitle}
          builtAt={card.built_at}
          model={card.model}
          stale={Boolean(card.stale)}
          classification={
            node ? (
              <FileCardClassification individualId={individualId} node={node} />
            ) : undefined
          }
        />
      </div>
    );
  } else if (isError) {
    body = <p className="text-base text-fg-3">Couldn't load this card.</p>;
  } else if (card && isCardBuilding(card.status)) {
    body = (
      <p className="flex items-center gap-2 text-base text-fg-3">
        <Spinner className="size-3.5" />
        Building the card…
      </p>
    );
  } else if (card?.status === "unavailable") {
    body = <p className="text-base text-fg-3">{unavailableMessage(card)}</p>;
  } else if (card) {
    body = <p className="text-base text-fg-3">This file has no card yet.</p>;
  }

  return (
    <div
      data-testid="file-card-pane"
      className="h-full overflow-y-auto bg-surface"
    >
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-3 px-8 py-8">
        {card && card.status !== "unavailable" ? (
          <div className="flex justify-end">
            <Button
              variant="ghost"
              size="xs"
              onClick={askCasey}
              data-testid="file-card-ask-casey"
            >
              <Sparkles className="size-3.5" />
              Ask Casey to Edit
            </Button>
          </div>
        ) : null}
        {body}
      </div>
    </div>
  );
};
